import axios from "axios";
import { BASE_URL } from "./proApi";
import { RAZORPAY_BRAND } from "../utils/razorpayBranding";

// =====================================================
// THEBLOGSSPHERE PRO — PAID CREATOR SYSTEM, CLIENT API
//
// One module for every endpoint the paid Pro system exposes, so each screen
// shares the same base URL and the same auth header instead of re-declaring
// them (proApi.js, boostApi.js and referralApi.js already follow this pattern).
//
// NOTHING IN THIS FILE DECIDES ANYTHING.
//   Not access, not expiry, not money. Every function here asks the server and
//   renders the answer. The prices and durations below are LABELS — the server
//   reads its own copy from config/proConfig.js when it creates an order and
//   again when it verifies one, so editing these constants in a browser
//   changes what a button says and nothing else.
// =====================================================

export { BASE_URL };

// Display-only. The authoritative values live on the server.
export const ACCESS_PRICE_RUPEES = 19;
export const ACCESS_DURATION_DAYS = 30;
export const FREE_PREVIEW_COUNT = 2;

function authConfig(extra = {}) {
  const token = localStorage.getItem("token");
  return {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
}

const get = async (path) => (await axios.get(`${BASE_URL}${path}`, authConfig())).data;
const post = async (path, body) =>
  (await axios.post(`${BASE_URL}${path}`, body || {}, authConfig())).data;
const put = async (path, body) =>
  (await axios.put(`${BASE_URL}${path}`, body || {}, authConfig())).data;
const del = async (path) => (await axios.delete(`${BASE_URL}${path}`, authConfig())).data;

// =====================================================
// READING PRO CONTENT  (Steps 6, 7, 8, 14)
// =====================================================

// A creator's public Pro page: identity, the two counters, and every blog
// already resolved to locked or unlocked for the caller by the server.
export const getCreatorProPage = (creatorId) => get(`/api/pro-blogs/creator/${creatorId}`);

// One Pro blog. This is also what a share link opens — the server returns the
// full blog or the locked preview depending on who is asking.
export const getProBlog = (blogId) => get(`/api/pro-blogs/${blogId}`);

// Backs the "Watch Pro" button on the free author profile (Step 7).
export const checkProCreator = (creatorId) => get(`/api/pro-blogs/is-creator/${creatorId}`);

// =====================================================
// A CREATOR EDITING THEIR OWN PRO BLOGS
//
// The server re-checks ownership on both of these against the signed token —
// these are the convenient path, not the secure one.
// =====================================================

export const updateProBlog = (blogId, changes) => put(`/api/pro-blogs/${blogId}`, changes);

export const deleteProBlog = (blogId) => del(`/api/pro-blogs/${blogId}`);

// =====================================================
// ENGAGEMENT  (Step 12)
// =====================================================

export const toggleProLike = (blogId) => post(`/api/pro-blogs/${blogId}/like`);

export const shareProBlog = (blogId) => post(`/api/pro-blogs/${blogId}/share`);

// Told to the server AFTER a comment is posted through the existing free
// comment endpoints. The server re-checks that the comment really exists
// before it raises a notification — see the route for why.
export const reportProComment = (blogId) => post(`/api/pro-blogs/${blogId}/comment-posted`);

// =====================================================
// ACCESS AND PAYMENT  (Steps 9, 10, 11)
// =====================================================

export const createAccessOrder = (creatorId) =>
  post("/api/pro-access/create-order", { creatorId });

export const verifyAccessPayment = (payload) => post("/api/pro-access/verify", payload);

export const getMyProAccess = () => get("/api/pro-access/mine");

export const getAccessForCreator = (creatorId) => get(`/api/pro-access/creator/${creatorId}`);

// =====================================================
// CREATOR DASHBOARD  (Steps 15, 16, 17, 18)
// =====================================================

export const getMyProUsers = () => get("/api/pro-creator/users");

export const getMyEarnings = () => get("/api/pro-creator/earnings");

export const getMyEarningTransactions = () => get("/api/pro-creator/earnings/transactions");

export const getMyBank = () => get("/api/pro-creator/bank");

// `details` must include `otpTicket` — the single-use handle returned by
// POST /api/otp/verify. The server spends it BEFORE it writes anything, so a
// save without one stores nothing at all.
export const saveMyBank = (details) => put("/api/pro-creator/bank", details);

// Re-confirm the email on an already-saved account, without re-entering the
// account number. `otpTicket` replaces the Firebase ID token this used to
// take: email verification across the whole product is now the app's own
// Resend OTP system, and the browser never asserts verification itself — it
// hands over a ticket it cannot manufacture.
export const verifyBankEmail = (otpTicket) =>
  post("/api/pro-creator/bank/verify-email", { otpTicket });

// =====================================================
// NOTIFICATIONS  (Step 20)
// =====================================================

export const getProNotifications = (type) =>
  get(`/api/pro-notifications${type ? `?type=${encodeURIComponent(type)}` : ""}`);

export const markProNotificationsRead = () => post("/api/pro-notifications/read");

export const markProNotificationRead = (id) => post(`/api/pro-notifications/${id}/read`);

// =====================================================
// ADMIN  (Steps 23, 24, 25, 26, 27)
// =====================================================

export const adminProUsers = () => get("/api/admin/pro/users");
export const adminProCreators = () => get("/api/admin/pro/creators");
export const adminProEarnings = () => get("/api/admin/pro/earnings");
export const adminProTransactions = () => get("/api/admin/pro/transactions");

export const adminProPayouts = (params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v)
  ).toString();
  return get(`/api/admin/pro/payouts${query ? `?${query}` : ""}`);
};

export const adminCloseMonth = (monthKey) =>
  post("/api/admin/pro/payouts/close", monthKey ? { monthKey } : {});

export const adminSetPayoutStatus = (payoutId, body) =>
  post(`/api/admin/pro/payouts/${payoutId}/status`, body);

// `force: true` records the refund in the books WITHOUT calling Razorpay —
// only for a payment that was already refunded outside the app. The default
// (force omitted) really refunds the money through Razorpay.
export const adminRefundEarning = (earningId, reason, force = false) =>
  post(`/api/admin/pro/earnings/${earningId}/refund`, { reason, force });

// =====================================================
// RAZORPAY CHECKOUT  (Step 9)
//
// The ₹19 flow is the SAME three steps the ₹29 membership already uses, so
// there is one payment shape in this app rather than two:
//   1. ask the server for an order (it sets the amount and the creator);
//   2. open Razorpay with that order;
//   3. hand the signature back to the server, which verifies it and only then
//      grants access.
//
// The browser never decides that a payment succeeded. `onSuccess` fires on the
// SERVER's answer, not on Razorpay's callback.
// =====================================================

const RAZORPAY_SRC = "https://checkout.razorpay.com/v1/checkout.js";

export function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);

    const existing = document.querySelector(`script[src="${RAZORPAY_SRC}"]`);

    if (existing) {
      existing.addEventListener("load", () => resolve(Boolean(window.Razorpay)));
      existing.addEventListener("error", () => resolve(false));
      return undefined;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_SRC;
    script.async = true;
    script.onload = () => resolve(Boolean(window.Razorpay));
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
    return undefined;
  });
}

export async function payForCreatorAccess({ creatorId, creatorName, userName, onSuccess, onError, onClose }) {
  const ready = await loadRazorpay();

  if (!ready) {
    onError?.("Payment is still loading. Please try again in a moment.");
    return;
  }

  let order;

  try {
    order = await createAccessOrder(creatorId);
  } catch (err) {
    onError?.(err?.response?.data?.message || "Unable to start the payment");
    return;
  }

  const checkout = new window.Razorpay({
    key: order.key_id,
    amount: order.order.amount,
    currency: order.order.currency,
    ...RAZORPAY_BRAND,
    description: `Pro access — ${creatorName || order.creator?.name || "creator"}`,
    order_id: order.order.id,

    handler: async (response) => {
      try {
        // The server verifies the signature, re-fetches the order from
        // Razorpay and checks the amount and the owner before granting
        // anything. Only its answer counts.
        const verified = await verifyAccessPayment({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });

        if (verified?.success) onSuccess?.(verified);
        else onError?.(verified?.message || "Payment could not be verified");
      } catch (err) {
        onError?.(
          err?.response?.data?.message ||
            "Payment verification failed. If money was deducted, please contact support."
        );
      }
    },

    prefill: { name: userName || "" },
    theme: { color: "#8b5cf6" },
    modal: { ondismiss: () => onClose?.() },
  });

  checkout.on("payment.failed", (response) => {
    onError?.(response?.error?.description || "Payment failed. Please try again.");
  });

  checkout.open();
}

// =====================================================
// FORMATTING HELPERS
//
// Shared so "12 days 4 hours left" reads identically on My Pro Access (Step 10)
// and My Pro Users (Step 15) — the two screens showing the same fact from
// opposite sides.
// =====================================================

export function formatRemaining(ms) {
  const total = Math.max(0, Number(ms) || 0);
  if (total === 0) return "Expired";

  const days = Math.floor(total / 86400000);
  const hours = Math.floor((total % 86400000) / 3600000);
  const minutes = Math.floor((total % 3600000) / 60000);

  if (days > 0) return `${days} day${days === 1 ? "" : "s"} ${hours} hr`;
  if (hours > 0) return `${hours} hr ${minutes} min`;
  return `${minutes} min`;
}

export function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatMoney(rupees) {
  const n = Number(rupees) || 0;
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatMonth(monthKey) {
  if (!monthKey) return "—";
  const [year, month] = String(monthKey).split("-").map(Number);
  if (!year || !month) return monthKey;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
